import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ChatInterface } from '../../components/agents/ChatInterface';
import { AgentSelector } from '../../components/agents/AgentSelector';
import { ConversationHistory } from '../../components/agents/ConversationHistory';
import { useAgents } from '../../hooks/useAgents';
import { useProjectContext } from '../../contexts/ProjectContext';
import { 
  ArrowLeft, 
  MessageSquare, 
  Settings, 
  Share,
  Archive,
  MoreVertical
} from 'lucide-react';
import { cn } from '../../lib/utils';

const AGENTS = {
  data: { name: 'Analytics Agent', color: 'blue' },
  marketing: { name: 'Marketing Advisor', color: 'green' },
  strategy: { name: 'Strategy Consultant', color: 'purple' },
  persona: { name: 'Persona Expert', color: 'pink' },
  content: { name: 'Content Specialist', color: 'orange' }
};

export function ConversationPage() {
  const { agentType, conversationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentAgent, setCurrentAgent] = useState(agentType || 'data');
  const [showHistory, setShowHistory] = useState(false);
  
  // Get initial question from URL params
  const initialQuestion = searchParams.get('question');
  
  // Redirect if agentType is missing
  useEffect(() => {
    if (!agentType) {
      navigate('/agents/data/conversation', { replace: true });
    }
  }, [agentType, navigate]);
  
  const { useConversation } = useAgents();
  const { selectedProjectId } = useProjectContext();

  // Update currentAgent when agentType param changes
  useEffect(() => {
    if (agentType && agentType !== currentAgent) {
      setCurrentAgent(agentType);
    }
  }, [agentType]);

  // Clear question parameter from URL after ChatInterface has processed it
  useEffect(() => {
    if (initialQuestion && !conversationId) {
      // Remove question parameter after a delay to allow ChatInterface to process it
      const timer = setTimeout(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('question');
        setSearchParams(newSearchParams, { replace: true });
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [initialQuestion, conversationId, searchParams, setSearchParams]);

  const agent = AGENTS[currentAgent as keyof typeof AGENTS];

  const handleAgentChange = (newAgent: string) => {
    setCurrentAgent(newAgent);
    // Navigate to new agent conversation
    navigate(`/agents/${newAgent}/conversation`);
  };

  const handleNewConversation = () => {
    navigate(`/agents/${currentAgent}/conversation`);
  };

  const handleConversationCreated = (newConversationId: string) => {
    navigate(`/agents/${currentAgent}/conversation/${newConversationId}`);
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col lg:flex-row">
      {/* Mobile Header - Enhanced Design */}
      <div className="lg:hidden bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 safe-area-padding-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/agents')}
              className="h-9 px-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="text-sm">Back</span>
            </Button>
            
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-600" />
            
            <AgentSelector
              currentAgent={currentAgent}
              onAgentChange={handleAgentChange}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              className="h-9 w-9 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              aria-label="Show conversation history"
            >
              <MessageSquare className="w-4 h-4" />
            </Button>
            
            <Button
              onClick={handleNewConversation}
              size="sm"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 h-9 px-3 rounded-lg shadow-sm"
            >
              <span className="text-sm font-medium">New</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Sidebar - Enhanced Mobile Behavior */}
      <div className={cn(
        'transition-all duration-300 ease-in-out overflow-hidden border-r border-gray-200 dark:border-gray-700',
        // Mobile: overlay behavior with backdrop
        'lg:relative absolute inset-y-0 left-0 z-50 bg-white dark:bg-gray-900',
        // Responsive width
        showHistory ? 'w-full sm:w-80 lg:w-80' : 'w-0 lg:w-0',
        // Mobile shadow
        showHistory ? 'shadow-2xl lg:shadow-none' : ''
      )}>
        {/* Mobile backdrop overlay */}
        {showHistory && (
          <div 
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowHistory(false)}
            aria-label="Close sidebar"
          />
        )}
        
        <div className="h-full bg-white dark:bg-gray-900 relative z-50">
          {/* Sidebar Header - Enhanced Mobile */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white text-base">Conversations</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(false)}
              className="lg:hidden h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              aria-label="Close conversations"
            >
              <span className="text-lg">×</span>
            </Button>
          </div>
          <ConversationHistory 
            agentType={currentAgent}
            currentConversationId={conversationId}
            onConversationSelect={(id) => {
              navigate(`/agents/${currentAgent}/conversation/${id}`);
              setShowHistory(false); // Auto-close on mobile
            }}
          />
        </div>
      </div>

      {/* Main Chat Area - Enhanced Mobile Layout */}
      <div className="flex-1 flex flex-col min-w-0 bg-gray-50 dark:bg-gray-900">
        {/* Desktop Header - Enhanced Design */}
        <div className="hidden lg:block bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/agents')}
                className="hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Agents
              </Button>
              
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />
              
              <AgentSelector
                currentAgent={currentAgent}
                onAgentChange={handleAgentChange}
              />
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className="hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                History
              </Button>
              
              <Button 
                variant="ghost" 
                size="sm"
                className="hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <Share className="w-4 h-4 mr-2" />
                Share
              </Button>
              
              <Button 
                variant="ghost" 
                size="sm"
                className="hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
              
              <Button
                onClick={handleNewConversation}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-sm"
              >
                New Chat
              </Button>
            </div>
          </div>
        </div>

        {/* Chat Interface Container */}
        <div className="flex-1 overflow-hidden bg-white dark:bg-gray-900">
          <ChatInterface
            agentType={currentAgent}
            conversationId={conversationId}
            onNewConversation={handleNewConversation}
            onConversationCreated={handleConversationCreated}
            projectId={selectedProjectId || undefined}
            initialQuestion={initialQuestion}
          />
        </div>
      </div>
    </div>
  );
}